// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/CondutorSelect.tsx
//
//  Escolhe UM condutor do cadastro (nome + CPF), com busca. Trabalha pelo NOME
//  porque a carga grava o motorista como texto (s_motorista, igual ao Desktop):
//  quem usa id deve preferir o CondutorMultiSelect / combo por id.
//  Um nome antigo digitado à mão (fora do cadastro) continua aparecendo.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCondutoresCombo } from "@/features/transporte/hooks/useTransporte";

interface Props {
  /** Nome do condutor ("" = nenhum). */
  value: string;
  onChange: (nome: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const semAcento = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function CondutorSelect({ value, onChange, placeholder = "Selecione o motorista", disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const { data: condutores = [], isLoading } = useCondutoresCombo();

  const termo = semAcento(busca.trim());
  const digitos = busca.replace(/\D/g, "");
  const filtrados = condutores.filter((c) =>
    !termo || semAcento(c.nome).includes(termo) || (digitos.length >= 3 && (c.cpf ?? "").includes(digitos)));

  const escolher = (nome: string) => { onChange(nome); setOpen(false); setBusca(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" type="button" disabled={disabled}
            className={cn("w-full justify-between font-normal", value && "pr-14")}>
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {value || (isLoading ? "Carregando…" : placeholder)}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {value && !disabled && (
          <button type="button" aria-label="Limpar motorista" onClick={() => onChange("")}
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-[min(420px,90vw)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar por nome ou CPF..." value={busca} onValueChange={setBusca} />
          <CommandList>
            <CommandEmpty>
              {condutores.length === 0 ? "Nenhum condutor cadastrado (Cadastros › Condutores)." : "Nenhum condutor encontrado."}
            </CommandEmpty>
            <CommandGroup>
              {filtrados.map((c) => (
                <CommandItem key={c.id} value={String(c.id)} onSelect={() => escolher(c.nome)}>
                  <Check className={cn("mr-2 h-4 w-4", value === c.nome ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col">
                    <span>{c.nome}</span>
                    {c.cpf && <span className="text-xs text-muted-foreground">CPF {c.cpf}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
